import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api.js';
import type { AnalyzeResult, LayoutMapping, Settings, SettingsAccount } from '../../lib/settings-types.js';
import { MappingEditor, mappingProblems } from '../../components/MappingEditor.js';
import { Button, Combobox, Field, Modal, Select, TextInput, useConfirm, Checkbox } from '../../components/ui/index.js';
import { errorMessage, HOURS, useIsAdmin } from './common.js';

/** Providers whose account IS a URL. Uploads and emailed reports are pushed in. */
const URL_PROVIDERS = ['swaarm', 'aemdays', 'justforspends', 'trafficguru', 'generic'];

/** How a source that has no URL actually arrives, so the dialog can say so. */
const NO_URL_REASON: Record<string, string> = {
  upload: 'This source is fed by uploaded files, so it has no URL to fix.',
  email: 'This source is fed by emailed reports, so it has no URL to fix.',
};

interface Draft {
  label: string;
  ownerUserId: number | null;
  fetchHour: number | null;
  currencyMode: string;
  fxBasis: string;
  followWorkbook: boolean;
  url: string;
}

/**
 * Everything about one source, in one place, behind Edit.
 *
 * It replaces Rename, which could change the one field that never mattered
 * when something was wrong. The field that DID matter — the URL, and the token
 * in it — was not editable at all: fixing an expired credential meant going to
 * Add stats and pasting the source in again as if it were new, which is a
 * strange thing to ask of someone whose only problem is a rotated key.
 *
 * The URL box starts EMPTY on purpose. The stored one holds a secret and the
 * settings read never returns decrypted secrets (only the masked preview shown
 * beneath it), so there is nothing honest to prefill: blank means keep, and
 * anything you type replaces the credentials wholesale. Saving a new URL
 * re-pulls immediately — the answer to "did that fix it?" is the point of the
 * exercise, so the dialog stays open and shows it when the pull still fails.
 */
export function SourceEditDialog({
  account: a,
  settings,
  onClose,
}: {
  account: SettingsAccount;
  settings: Settings;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const [draft, setDraft] = useState<Draft>({
    label: a.label ?? '',
    ownerUserId: a.ownerUserId,
    fetchHour: a.fetchHour,
    currencyMode: a.currencyMode,
    fxBasis: a.fxBasis,
    followWorkbook: a.followWorkbook,
    url: '',
  });
  const [busy, setBusy] = useState<null | 'saving' | 'analyzing' | 'pulling'>(null);
  // Owner rule (2026-09-12): a new URL is a new layout. Save first reads the
  // URL and shows the columns it proposes (or recalls); only "Confirm columns
  // and save" stores anything, under the new URL's own key.
  const [review, setReview] = useState<{ analysis: AnalyzeResult; mapping: LayoutMapping | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]): void => {
    setDraft((d) => ({ ...d, [key]: value }));
  };

  const isAdmin = useIsAdmin();
  // The URL carries the credentials: changing it is a shared-secret write and
  // admin-only, like Replace credentials (the server refuses it otherwise).
  // Everyone else edits the name, the user, the hour and the currency.
  const hasUrl = URL_PROVIDERS.includes(a.provider);
  const editableUrl = hasUrl && isAdmin;
  const newUrl = draft.url.trim();
  const reviewProblems = review?.mapping ? mappingProblems(review.mapping) : [];
  // A built-in layout is applied as-is (nothing to edit); anything else needs
  // a mapping without problems before it can be stored.
  const reviewReady =
    !!review && (review.analysis.mode === 'builtin' || (!!review.mapping && reviewProblems.length === 0));

  /** Only what actually changed — an unchanged field is not a save. */
  function settingsPatch(): Record<string, unknown> {
    const patch: Record<string, unknown> = {};
    const label = draft.label.trim();
    if (label && label !== (a.label ?? '')) patch.label = label;
    if (draft.ownerUserId !== a.ownerUserId) patch.ownerUserId = draft.ownerUserId;
    if (draft.fetchHour !== a.fetchHour) patch.fetchHour = draft.fetchHour;
    if (draft.currencyMode !== a.currencyMode) patch.currencyMode = draft.currencyMode;
    if (draft.fxBasis !== a.fxBasis) patch.fxBasis = draft.fxBasis;
    if (draft.followWorkbook !== a.followWorkbook) patch.followWorkbook = draft.followWorkbook;
    return patch;
  }

  /**
   * Fixing a currency over history labelled differently has two valid answers,
   * so the server refuses with a 409 until it is told which. Same question the
   * card asked before, now raised from inside the dialog — dialogs stack, and
   * the newer one takes focus.
   */
  async function saveSettings(patch: Record<string, unknown>): Promise<void> {
    try {
      await api.post('/api/settings/account', { accountId: a.id, ...patch });
    } catch (err) {
      const body =
        err instanceof ApiError
          ? (err.body as { error?: string; rowCount?: number; currencies?: string[] })
          : null;
      if (body?.error !== 'currency-decision-required') throw err;
      const choice = await confirm<'convert' | 'keep'>({
        title: 'This source already has history in another currency',
        body: (
          <p>
            {body.rowCount} stored row(s) are labelled {(body.currencies ?? []).join(', ')}. New
            data will be {draft.currencyMode} either way — what should happen to those?
          </p>
        ),
        choices: [
          { label: `Relabel them as ${draft.currencyMode}`, value: 'convert' },
          { label: 'Keep their labels', value: 'keep', variant: 'ghost' },
        ],
        cancelLabel: 'Leave the currency alone',
      });
      if (choice == null) {
        // Backing out of the follow-up means the currency change is off; the
        // rest of the edit still stands.
        const rest = { ...patch };
        delete rest.currencyMode;
        if (Object.keys(rest).length) await saveSettings(rest);
        set('currencyMode', a.currencyMode);
        return;
      }
      await api.post('/api/settings/account', { accountId: a.id, ...patch, pastRows: choice });
    }
  }

  async function save(): Promise<void> {
    setError(null);
    setOutcome(null);
    const patch = settingsPatch();
    if (!Object.keys(patch).length && !newUrl) {
      onClose();
      return;
    }
    try {
      if (newUrl && !review) {
        // Step one for a new URL: read it and propose (or recall) the layout.
        // Nothing is stored until the columns are confirmed below.
        setBusy('analyzing');
        const analysis = await api.post<AnalyzeResult>('/api/add-source/analyze', {
          url: newUrl,
          forceGeneric: a.provider === 'generic',
        });
        if (analysis.mode === 'empty') {
          setError('The URL answers but has no rows yet — there is nothing to confirm. Try again once it has data.');
          return;
        }
        setReview({ analysis, mapping: analysis.mapping ? structuredClone(analysis.mapping) : null });
        return;
      }
      if (Object.keys(patch).length) {
        setBusy('saving');
        await saveSettings(patch);
      }
      if (newUrl) {
        // The URL goes through Add stats' own path with the CONFIRMED mapping:
        // it derives the credentials, stores the layout under the new URL's
        // key, updates this account in place (history stays attached), clears
        // the old error and pulls once.
        setBusy('pulling');
        const res = await api.post<{ rows: number; warning: string | null }>('/api/add-source', {
          accountId: a.id,
          owner: a.rep,
          url: review?.analysis.canonicalUrl ?? newUrl,
          forceGeneric: a.provider === 'generic',
          ...(draft.label.trim() ? { label: draft.label.trim() } : {}),
          ...(review && review.analysis.mode !== 'builtin' && review.mapping ? { mapping: review.mapping } : {}),
          ...((review?.analysis.snapshotColumns ?? review?.analysis.columns)
            ? { columns: review!.analysis.snapshotColumns ?? review!.analysis.columns }
            : {}),
        });
        setReview(null);
        if (res.warning) {
          // Saved, but the source still will not pull — the one outcome that
          // must not look like success, and the reason to stay on screen.
          setOutcome(null);
          setError(`Saved the new URL, but the pull failed: ${res.warning}`);
          setDraft((d) => ({ ...d, url: '' }));
          return;
        }
        // A successful pull is the answer to "did that fix it?", so it stays
        // on screen rather than vanishing with the dialog.
        setOutcome(`Pulled ${res.rows} row${res.rows === 1 ? '' : 's'} with the new URL.`);
        setDraft((d) => ({ ...d, url: '' }));
        return;
      }
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(null);
      void qc.invalidateQueries({ queryKey: ['settings'] });
      void qc.invalidateQueries({ queryKey: ['summary'] });
    }
  }

  return (
    <>
      <Modal
        open
        title={`Edit ${a.label ?? a.id}`}
        description={
          <>
            {a.providerLabel ?? a.provider} · {a.rep} · {a.id}
          </>
        }
        onClose={busy ? () => {} : onClose}
        actions={
          <>
            <Button variant="ghost" onClick={onClose} disabled={!!busy}>
              {outcome || error ? 'Close' : 'Cancel'}
            </Button>
            <Button onClick={() => void save()} loading={!!busy} disabled={!!review && !reviewReady}>
              {busy === 'pulling'
                ? 'Pulling…'
                : busy === 'analyzing'
                  ? 'Reading the source…'
                  : review
                    ? 'Confirm columns and save'
                    : 'Save changes'}
            </Button>
          </>
        }
      >
        <div className="cfg-grid">
          <Field label="Name" hint="What this source is called on the dashboard.">
            {(aria) => (
              <TextInput
                {...aria}
                value={draft.label}
                placeholder={a.id}
                onChange={(e) => set('label', e.target.value)}
              />
            )}
          </Field>

          <Field label="Dashboard user">
            {(aria) => (
              <Select
                {...aria}
                value={draft.ownerUserId ?? ''}
                onChange={(e) =>
                  set('ownerUserId', e.target.value === '' ? null : Number(e.target.value))
                }
              >
                <option value="">— nobody —</option>
                {settings.users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.username}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field label="Crawl hour">
            {(aria) => (
              <Select
                {...aria}
                value={draft.fetchHour ?? ''}
                onChange={(e) =>
                  set('fetchHour', e.target.value === '' ? null : Number(e.target.value))
                }
              >
                <option value="">{`global (${settings.defaultFetchHour}:00)`}</option>
                {HOURS.map((h) => (
                  <option key={h} value={h}>
                    {h}:00
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field
            label="Currency"
            hint="Auto reads currency markers in the data and assumes USD when there are none. A fixed code labels every row."
          >
            {(aria) => (
              <Combobox
                {...aria}
                value={draft.currencyMode}
                onChange={(v) => set('currencyMode', v)}
                options={[
                  { value: 'multi', label: 'Auto', hint: 'from the data' },
                  ...settings.currencies.map((c) => ({ value: c, label: c })),
                ]}
              />
            )}
          </Field>

          <Field
            label="Rate day"
            hint="Which day's FX rate values a row. Stat day is steadier for API-crawled sources."
          >
            {(aria) => (
              <Select {...aria} value={draft.fxBasis} onChange={(e) => set('fxBasis', e.target.value)}>
                <option value="received">Rate on the day received</option>
                <option value="stat-day">Rate on the stat's own day</option>
              </Select>
            )}
          </Field>

          {a.provider === 'generic' && /docs\.google\.com/.test(a.editUrl ?? '') ? (
            <Checkbox
              className="cfg-wide"
              label="Follow the workbook"
              hint="On for every Google Sheet unless you untick it: each pull looks at the workbook's tabs, and when a later tab has exactly the learned columns and its rows start after the current tab's last day, the source moves to it — a provider that opens a new tab each month is followed on its own, and last month's late corrections are still read. A newer tab with different columns is reported, never read, until you point the source at it. Needs a Google account connected under Email & Sheets that can open the workbook; without one the pull reads only the tab this link names and says so. Untick to read only this tab."
              checked={draft.followWorkbook}
              onChange={(e) => set('followWorkbook', e.target.checked)}
            />
          ) : null}
        </div>

        {editableUrl ? (
          <Field
            className="cfg-wide"
            label="Source URL"
            hint="Paste the full URL, including its token or key — it replaces the stored credentials and pulls once to check. Leave it blank to keep the current one."
          >
            {(aria) => (
              <TextInput
                {...aria}
                mono
                value={draft.url}
                placeholder="https://…"
                onChange={(e) => {
                  set('url', e.target.value);
                  // A different URL is a different layout: back to step one.
                  if (review) setReview(null);
                }}
              />
            )}
          </Field>
        ) : (
          <p className="cfg-note">
            {hasUrl
              ? 'Only an admin can change the URL or credentials of this source - they are shared secrets.'
              : (NO_URL_REASON[a.provider] ?? 'This source has no URL.')}
          </p>
        )}

        {editableUrl && a.editUrl ? (
          <p className="cfg-url">
            Currently <code>{a.editUrl}</code> — secrets masked, they are never sent back to the
            browser.
          </p>
        ) : null}

        {review ? (
          <div className="cfg-callout" role="group" aria-label="Review the columns">
            <p className="table-note">
              <b>Review the columns</b> —{' '}
              {review.analysis.mode === 'builtin'
                ? `Recognised as ${review.analysis.system} — the built-in field mapping below is applied.`
                : review.analysis.mode === 'template'
                  ? 'A layout for this address is already learned — review and confirm.'
                  : 'Proposed column matching (one-time learn) — review and confirm.'}{' '}
              Confirm the columns before anything is stored; the layout is kept under this URL, tab included.
            </p>
            {review.mapping ? (
              <MappingEditor
                columns={review.analysis.columns ?? []}
                samples={review.analysis.samples ?? {}}
                mapping={review.mapping}
                onChange={(m) => setReview((r) => (r ? { ...r, mapping: m } : r))}
                readOnly={review.analysis.mode === 'builtin'}
              />
            ) : null}
          </div>
        ) : null}

        {error ? <p className="form-err">{error}</p> : null}
        {outcome ? <p className="form-ok">{outcome}</p> : null}
      </Modal>
      {dialog}
    </>
  );
}
